/**
 * LoginPage
 *
 * Sign-in page with Google OAuth button.
 * Redirects authenticated users to home.
 */

import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

export default function LoginPage() {
  const { status, loginWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const redirectTo = searchParams.get('redirect_to') ?? '/'
  const error = searchParams.get('error')

  // Already authenticated → redirect
  useEffect(() => {
    if (status === 'authenticated') {
      void navigate(redirectTo, { replace: true })
    }
  }, [status, navigate, redirectTo])

  const errorMessages: Record<string, string> = {
    access_denied: 'Login was cancelled.',
    invalid_request: 'Invalid login request. Please try again.',
    invalid_state: 'Security validation failed. Please try again.',
    token_exchange_failed: 'Failed to connect to Google. Please try again.',
    userinfo_failed: 'Could not retrieve your Google account info.',
    email_not_verified: 'Your Google account email is not verified.',
  }

  return (
    <div className="min-h-[calc(100vh-48px)] flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
          {/* Logo + title */}
          <div className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Sign in to Check My Rig</h1>
            <p className="text-sm text-gray-500">Save your builds and favorites across devices</p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {errorMessages[error] ?? 'An error occurred. Please try again.'}
            </div>
          )}

          {/* Google sign-in button */}
          <button
            onClick={() => loginWithGoogle(redirectTo)}
            disabled={status === 'loading'}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* Google "G" logo */}
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {status === 'loading' ? 'Checking session...' : 'Continue with Google'}
          </button>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400">
            By signing in you agree to our terms of service.
          </p>
        </div>
      </div>
    </div>
  )
}
